import React, { useState, useEffect } from 'react';
import '../../styles/ProjectFormPremium.scss';
import RichTextEditor from './RichTextEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const BASE_URL = API_BASE.replace(/\/api\/?$/, '');

const ProjectForm = ({ onSubmit, editingProject = null, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentSection, setCurrentSection] = useState(1);
  
  const [formData, setFormData] = useState({
    // Basic Info
    title: '',
    category: '',
    domain: '',
    
    // Descriptions
    description_1: '',
    description_2: '',
    description_3: '',
    description_4: '',
    description_5: '',
    description_6: '',
    
    // Details
    features: '',
    about: '',
    tech_stack: '',
    setup_info: '',
    
    // Media (URLs from uploads)
    images: '',  // JSON array of URLs
    demo_video_url: '',
    installation_video_url: '',
    
    // Files (URLs from uploads)
    report_file: '',
    zip_file: '',
    
    // Developer
    developer_name: '',
    developer_image: '',
    developer_intro: '',
    
    // Pricing & Contact
    price: 0,
    originalPrice: '',
    discountedPrice: '',
    isDiscountActive: false,
    whatsapp_number: '9762424851',
    contact_email: 'info@pravarainfotech.in',
    is_active: true,
  });

  // Track uploaded file previews
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState({
    demo: null,
    installation: null,
  });
  const [filePreviews, setFilePreviews] = useState({
    report: null,
    zip: null,
  });

  useEffect(() => {
    if (editingProject) {
      setFormData({
        title: editingProject.title || '',
        category: editingProject.category || '',
        domain: editingProject.domain || '',
        description_1: editingProject.description_1 || '',
        description_2: editingProject.description_2 || '',
        description_3: editingProject.description_3 || '',
        description_4: editingProject.description_4 || '',
        description_5: editingProject.description_5 || '',
        description_6: editingProject.description_6 || '',
        features: editingProject.features || '',
        about: editingProject.about || '',
        tech_stack: editingProject.tech_stack || '',
        setup_info: editingProject.setup_info || '',
        images: editingProject.images ? JSON.stringify(editingProject.images) : '',
        demo_video_url: editingProject.demo_video_url || '',
        installation_video_url: editingProject.installation_video_url || '',
        report_file: editingProject.report_file || '',
        zip_file: editingProject.zip_file || '',
        developer_name: editingProject.developer_name || '',
        developer_image: editingProject.developer_image || '',
        developer_intro: editingProject.developer_intro || '',
        price: editingProject.price || 0,
        originalPrice: editingProject.originalPrice ?? '',
        discountedPrice: editingProject.discountedPrice ?? '',
        isDiscountActive: editingProject.isDiscountActive ?? false,
        whatsapp_number: editingProject.whatsapp_number || '9762424851',
        contact_email: editingProject.contact_email || 'info@pravarainfotech.in',
        is_active: editingProject.is_active !== undefined ? editingProject.is_active : true,
      });

      // Set previews for existing files
      if (editingProject.images) {
        const imgs = Array.isArray(editingProject.images) ? editingProject.images : [];
        setImagePreviews(imgs);
      }
      if (editingProject.demo_video_url) {
        setVideoPreviews(prev => ({ ...prev, demo: editingProject.demo_video_url }));
      }
      if (editingProject.installation_video_url) {
        setVideoPreviews(prev => ({ ...prev, installation: editingProject.installation_video_url }));
      }
      if (editingProject.report_file) {
        setFilePreviews(prev => ({ ...prev, report: editingProject.report_file }));
      }
      if (editingProject.zip_file) {
        setFilePreviews(prev => ({ ...prev, zip: editingProject.zip_file }));
      }
    }
  }, [editingProject]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Handle image uploads
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (imagePreviews.length + files.length > 6) {
      setError('Maximum 6 images allowed per project');
      return;
    }

    setUploading(true);
    try {
      const formDataObj = new FormData();
      files.forEach(file => formDataObj.append('files', file));

      const response = await fetch(`${API_BASE}/projects/upload/images`, {
        method: 'POST',
        body: formDataObj,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Image upload failed');
      }

      const data = await response.json();
      const newImages = [...imagePreviews, ...data.files];
      setImagePreviews(newImages);
      setFormData(prev => ({
        ...prev,
        images: JSON.stringify(newImages)
      }));
    } catch (err) {
      setError(err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Handle video uploads
  const handleVideoUpload = async (e, videoType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();

// 🔥 FORCE STRING (important)
      formDataObj.append("video_type", String(videoType));
      formDataObj.append("file", file);

      console.log("Sending:", videoType, file);

      const response = await fetch(`${API_BASE}/projects/upload/videos`, {
        method: 'POST',
        body: formDataObj,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Video upload failed');
      }

      const data = await response.json();
      const fieldName = videoType === 'demo' ? 'demo_video_url' : 'installation_video_url';
      setFormData(prev => ({
        ...prev,
        [fieldName]: data.file
      }));
      setVideoPreviews(prev => ({
        ...prev,
        [videoType]: data.file
      }));
    } catch (err) {
      setError(err.message || 'Failed to upload video');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Handle file uploads (report, zip)
  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file_type', fileType);
      formDataObj.append('file', file);

      const response = await fetch(`${API_BASE}/projects/upload/files`, {
        method: 'POST',
        body: formDataObj,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'File upload failed');
      }

      const data = await response.json();
      const fieldName = fileType === 'report' ? 'report_file' : 'zip_file';
      setFormData(prev => ({
        ...prev,
        [fieldName]: data.file
      }));
      setFilePreviews(prev => ({
        ...prev,
        [fileType]: data.file
      }));
    } catch (err) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Remove image
  const removeImage = (index) => {
    const newImages = imagePreviews.filter((_, i) => i !== index);
    setImagePreviews(newImages);
    setFormData(prev => ({
      ...prev,
      images: JSON.stringify(newImages)
    }));
  };

  // Remove video
  const removeVideo = (videoType) => {
    const fieldName = videoType === 'demo' ? 'demo_video_url' : 'installation_video_url';
    setFormData(prev => ({
      ...prev,
      [fieldName]: ''
    }));
    setVideoPreviews(prev => ({
      ...prev,
      [videoType]: null
    }));
  };

  // Remove file
  const removeFile = (fileType) => {
    const fieldName = fileType === 'report' ? 'report_file' : 'zip_file';
    setFormData(prev => ({
      ...prev,
      [fieldName]: ''
    }));
    setFilePreviews(prev => ({
      ...prev,
      [fileType]: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.title.trim() || !formData.category.trim() || !formData.domain.trim()) {
        setError('Project name, technology, and domain are required');
        setLoading(false);
        return;
      }

      const origPrice = Number(formData.originalPrice);
      const discPrice = formData.discountedPrice !== '' && formData.discountedPrice !== null ? Number(formData.discountedPrice) : null;
      const isDisc = formData.isDiscountActive;

      if (isNaN(origPrice) || origPrice < 0) {
        setError('Original Price cannot be negative');
        setLoading(false);
        return;
      }

      if (isDisc) {
        if (discPrice === null || isNaN(discPrice)) {
          setError('Discounted Price is required when discount is enabled');
          setLoading(false);
          return;
        }
        if (discPrice >= origPrice) {
          setError('Discounted Price must be less than Original Price');
          setLoading(false);
          return;
        }
        if (discPrice < 0) {
          setError('Discounted Price cannot be negative');
          setLoading(false);
          return;
        }
      }

      const submitData = {
        ...formData,
        originalPrice: origPrice,
        discountedPrice: isDisc ? discPrice : null,
        isDiscountActive: isDisc,
        price: isDisc ? discPrice : origPrice,
        discount_price: isDisc ? discPrice : null,
      };

      await onSubmit(submitData);
      setSuccess(editingProject ? 'Project updated successfully!' : 'Project created successfully!');
      
      // Reset form if creating new
      if (!editingProject) {
        setFormData({
          title: '',
          category: '',
          domain: '',
          description_1: '',
          description_2: '',
          description_3: '',
          description_4: '',
          description_5: '',
          description_6: '',
          features: '',
          about: '',
          tech_stack: '',
          setup_info: '',
          images: '',
          demo_video_url: '',
          installation_video_url: '',
          report_file: '',
          zip_file: '',
          developer_name: '',
          developer_image: '',
          developer_intro: '',
          price: 0,
          originalPrice: '',
          discountedPrice: '',
          isDiscountActive: false,
          whatsapp_number: '9762424851',
          contact_email: 'info@pravarainfotech.in',
          is_active: true,
        });
        setImagePreviews([]);
        setVideoPreviews({ demo: null, installation: null });
        setFilePreviews({ report: null, zip: null });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-form-container">
      {/* STICKY HEADER WITH PROGRESS */}
      <div className="form-sticky-header">
        <div className="header-content">
          <h1 className="form-title">
            {editingProject ? '✏️ Update Project' : '🚀 Create New Project'}
          </h1>
          <p className="form-subtitle">Build your portfolio and share your amazing project with the world</p>
        </div>
        
        {/* PROGRESS INDICATOR */}
        <div className="progress-indicator">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(currentSection / 3) * 100}%` }}></div>
          </div>
          <span className="progress-text">Step {currentSection} of 3</span>
        </div>
      </div>
      

      {/* ALERTS */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span>{success}</span>
        </div>
      )}

      {/* MAIN FORM */}
      <form onSubmit={handleSubmit} className="premium-form">

        {/* SECTION 1: BASIC INFORMATION */}
        <div className="form-card" onMouseEnter={() => setCurrentSection(1)}>
          <div className="card-header">
            <div className="section-badge">1</div>
            <div className="header-info">
              <h2 className="card-title">📋 Basic Information</h2>
              <p className="card-subtitle">Project name and domain</p>
            </div>
          </div>

          <div className="card-content">
            <div className="form-grid-2">
              <div className="form-group">
                <label htmlFor="title" className="form-label">Project Name *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Hospital Management System"
                  className="form-input"
                  required
                />
                <span className="input-hint">Give your project an attractive, memorable name</span>
              </div>

              <div className="form-group">
                <label htmlFor="category" className="form-label">Technology Domain *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Technology Domain</option>
                  {[
                    'AI & Machine Learning',
                    'Web Development',
                    'Android App Development',
                    'Data Science & Data Analytics',
                    'IoT & Embedded Systems',
                    'Cloud Computing & DevOps',
                    'Cyber Security & Ethical Hacking',
                    'Blockchain & Cryptography',
                    'Desktop Application Development',
                    'Game Development',
                    'Other'
                  ].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <span className="input-hint">Choose the primary technology domain</span>
              </div>

              <div className="form-group">
                <label htmlFor="domain" className="form-label">Academic Domain *</label>
                <select
                  id="domain"
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Domain</option>
                  {['BCA/BCS/MCA', 'BE/B.Tech/ME/M.Tech/Diploma'].map(dom => (
                    <option key={dom} value={dom}>{dom}</option>
                  ))}
                </select>
                <span className="input-hint">Select the degree level (BCA/BCS/MCA, BE/B.Tech/ME/M.Tech/Diploma)</span>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: PROJECT DESCRIPTION */}
        <div className="form-card" onMouseEnter={() => setCurrentSection(2)}>
          <div className="card-header">
            <div className="section-badge">2</div>
            <div className="header-info">
              <h2 className="card-title">📝 Project Description</h2>
              <p className="card-subtitle">Enter project details and abstract with formatting (like MBA projects)</p>
            </div>
          </div>

          <div className="card-content">
            <div className="form-group span-2" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Description / Abstract *</label>
              <RichTextEditor
                value={formData.about}
                onChange={(val) => setFormData(prev => ({ ...prev, about: val, description_1: val }))}
                placeholder="Enter project description, features, tech stack, and installation guide..."
              />
              <span className="input-hint">Copy and paste all formatted details here</span>
            </div>
          </div>
        </div>

        {/* SECTION 3: PROJECT IMAGES */}
        <div className="form-card" onMouseEnter={() => setCurrentSection(3)}>
          <div className="card-header">
            <div className="section-badge">3</div>
            <div className="header-info">
              <h2 className="card-title">🖼️ Project Images</h2>
              <p className="card-subtitle">Upload up to 6 screenshots (JPG, PNG)</p>
            </div>
          </div>

          <div className="card-content">
            <div className="form-group">
              <label htmlFor="images-upload" className="form-label">Upload Images</label>
              <div className="upload-zone">
                <input
                  type="file"
                  id="images-upload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading || imagePreviews.length >= 6}
                  className="upload-input"
                />
                <div className="upload-content">
                  <div className="upload-icon">🖼️</div>
                  <p className="upload-text">
                    {uploading ? 'Uploading...' : 'Drag & drop images here or click to browse'}
                  </p>
                  <p className="upload-hint">
                    Maximum 6 images • 5MB each • JPG, PNG, GIF
                  </p>
                </div>
              </div>
              <span className="input-hint">Current: {imagePreviews.length} / 6 images</span>
            </div>

            {/* IMAGE PREVIEWS */}
            {imagePreviews.length > 0 && (
              <div className="previews-section">
                <p className="preview-label">📸 Uploaded Images</p>
                <div className="preview-grid">
                  {imagePreviews.map((url, idx) => (
                    <div key={idx} className="preview-item">
                      <img
                        src={`${BASE_URL}${url}`}
                        alt={`Preview ${idx + 1}`}
                        className="preview-image"
                      />
                      <div className="preview-overlay">
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeImage(idx)}
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 5: PROJECT VIDEOS (Removed)
        <div className="form-card" onMouseEnter={() => setCurrentSection(5)}>
          <div className="card-header">
            <div className="section-badge">5</div>
            <div className="header-info">
              <h2 className="card-title">🎬 Project Videos</h2>
              <p className="card-subtitle">Demo and installation videos (MP4, WebM)</p>
            </div>
          </div>

          <div className="card-content">
            <div className="videos-grid">
              // DEMO VIDEO
              <div className="form-group">
                <label htmlFor="demo-video-upload" className="form-label">Demo Video</label>
                <div className="upload-zone">
                  <input
                    type="file"
                    id="demo-video-upload"
                    accept="video/*"
                    onChange={(e) => handleVideoUpload(e, "demo")}
                    disabled={uploading}
                    className="upload-input"
                  />
                  <div className="upload-content">
                    <div className="upload-icon">🎥</div>
                    <p className="upload-text">Click or drag demo video</p>
                    <p className="upload-hint">Max 100MB • MP4, WebM, MOV</p>
                  </div>
                </div>

                {videoPreviews.demo && (
                  <div className="video-preview">
                    <video width="100%" height="auto" controls className="preview-video">
                      <source src={`${BASE_URL}${videoPreviews.demo}`} />
                    </video>
                    <button
                      type="button"
                      className="remove-btn btn-remove-video"
                      onClick={() => removeVideo("demo")}
                    >
                      ✕ Remove
                    </button>
                  </div>
                )}
              </div>

              // INSTALLATION VIDEO
              <div className="form-group">
                <label htmlFor="installation-video-upload" className="form-label">Installation Video</label>
                <div className="upload-zone">
                  <input
                    type="file"
                    id="installation-video-upload"
                    accept="video/*"
                    onChange={(e) => handleVideoUpload(e, "installation")}
                    disabled={uploading}
                    className="upload-input"
                  />
                  <div className="upload-content">
                    <div className="upload-icon">⚙️</div>
                    <p className="upload-text">Click or drag installation video</p>
                    <p className="upload-hint">Max 100MB • MP4, WebM, MOV</p>
                  </div>
                </div>

                {videoPreviews.installation && (
                  <div className="video-preview">
                    <video width="100%" height="auto" controls className="preview-video">
                      <source src={`${BASE_URL}${videoPreviews.installation}`} />
                    </video>
                    <button
                      type="button"
                      className="remove-btn btn-remove-video"
                      onClick={() => removeVideo("installation")}
                    >
                      ✕ Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        */}

        {/* SECTION 6: PROJECT FILES (Removed)
        <div className="form-card" onMouseEnter={() => setCurrentSection(6)}>
          <div className="card-header">
            <div className="section-badge">6</div>
            <div className="header-info">
              <h2 className="card-title">📦 Project Files</h2>
              <p className="card-subtitle">Report (PDF) and source code (ZIP)</p>
            </div>
          </div>

          <div className="card-content">
            <div className="files-grid">
              // REPORT FILE
              <div className="form-group">
                <label htmlFor="report-upload" className="form-label">📄 Project Report</label>
                <div className="upload-zone">
                  <input
                    type="file"
                    id="report-upload"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, 'report')}
                    disabled={uploading}
                    className="upload-input"
                  />
                  <div className="upload-content">
                    <div className="upload-icon">📋</div>
                    <p className="upload-text">Click or drag PDF file</p>
                    <p className="upload-hint">Max 25MB • PDF only</p>
                  </div>
                </div>

                {filePreviews.report && (
                  <div className="file-status">
                    <span className="status-icon">✅</span>
                    <span className="status-text">Report uploaded successfully</span>
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeFile('report')}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              // ZIP FILE
              <div className="form-group">
                <label htmlFor="zip-upload" className="form-label">🗜️ Source Code ZIP</label>
                <div className="upload-zone">
                  <input
                    type="file"
                    id="zip-upload"
                    accept=".zip"
                    onChange={(e) => handleFileUpload(e, 'zip')}
                    disabled={uploading}
                    className="upload-input"
                  />
                  <div className="upload-content">
                    <div className="upload-icon">📦</div>
                    <p className="upload-text">Click or drag ZIP file</p>
                    <p className="upload-hint">Max 100MB • ZIP only</p>
                  </div>
                </div>

                {filePreviews.zip && (
                  <div className="file-status">
                    <span className="status-icon">✅</span>
                    <span className="status-text">ZIP file uploaded successfully</span>
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeFile('zip')}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        */}



        {/* STICKY ACTION BUTTONS */}
        <div className="form-actions-sticky">
          <button
            type="submit"
            className="btn btn-primary btn-submit"
            disabled={loading || uploading}
          >
            {loading ? '⏳ Submitting...' : uploading ? '📤 Uploading...' : editingProject ? '✏️ Update Project' : '🚀 Publish Project'}
          </button>
          {onCancel && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading || uploading}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
